import { IsoPoint, PlacedBlock, DrawingElement } from '@/app/types/cad';
import { transformPoint, getTransformedSockets, getTransformedLineEndpoints } from './transform';

export interface SnapResult {
  position: IsoPoint;
  snapTarget: {
    type: 'socket' | 'line';
    blockId: string;
    socketIndex?: number;
    lineIndex?: number;
    isStart?: boolean;
  } | null;
}

/**
 * Find the best snap position for a given position against all placed blocks
 * @param position - The position to snap
 * @param blocks - All placed blocks to snap against
 * @param excludeBlockId - Optional block ID to exclude from snapping
 * @param snapDistance - Maximum distance for snapping (default: 0.5 grid units)
 * @returns Snap result with position and optional snap target
 */
export function findBestSnap(
  position: IsoPoint,
  blocks: PlacedBlock[],
  excludeBlockId?: string,
  snapDistance: number = 0.5
): SnapResult {
  let bestSnap: SnapResult = { position, snapTarget: null };
  let minDistance = snapDistance;

  // Get all available sockets
  const allSockets: Array<{
    blockId: string;
    socketIndex: number;
    position: IsoPoint;
  }> = [];

  blocks.forEach((block) => {
    if (block.id === excludeBlockId) return;

    const transformedSockets = getTransformedSockets(
      block.blockData.elements,
      block.transform
    );

    transformedSockets.forEach((socket) => {
      allSockets.push({
        blockId: block.id,
        socketIndex: socket.index,
        position: socket.position,
      });
    });
  });

  // Check socket snapping
  allSockets.forEach((socket) => {
    const distance = Math.sqrt(
      Math.pow(socket.position.u - position.u, 2) +
      Math.pow(socket.position.v - position.v, 2)
    );

    if (distance < minDistance) {
      minDistance = distance;
      bestSnap = {
        position: socket.position,
        snapTarget: {
          type: 'socket',
          blockId: socket.blockId,
          socketIndex: socket.socketIndex,
        },
      };
    }
  });

  // Get all available line endpoints
  const allLineEndpoints: Array<{
    blockId: string;
    lineIndex: number;
    isStart: boolean;
    position: IsoPoint;
  }> = [];

  blocks.forEach((block) => {
    if (block.id === excludeBlockId) return;

    const transformedLines = getTransformedLineEndpoints(
      block.blockData.elements,
      block.transform
    );

    transformedLines.forEach((line) => {
      // Add start endpoint
      allLineEndpoints.push({
        blockId: block.id,
        lineIndex: line.index,
        isStart: true,
        position: line.startPosition,
      });

      // Add end endpoint
      allLineEndpoints.push({
        blockId: block.id,
        lineIndex: line.index,
        isStart: false,
        position: line.endPosition,
      });
    });
  });

  // Check line endpoint snapping
  allLineEndpoints.forEach((endpoint) => {
    const distance = Math.sqrt(
      Math.pow(endpoint.position.u - position.u, 2) +
      Math.pow(endpoint.position.v - position.v, 2)
    );

    if (distance < minDistance) {
      minDistance = distance;
      bestSnap = {
        position: endpoint.position,
        snapTarget: {
          type: 'line',
          blockId: endpoint.blockId,
          lineIndex: endpoint.lineIndex,
          isStart: endpoint.isStart,
        },
      };
    }
  });

  return bestSnap;
}
